import type React from 'react';
import type { CurrentUserDto } from '../../../../../types/UserDtos';
import { constObservable } from '../../../../services/observables';
import { useAppContext } from '../../../hooks/useAppContext';
import useLocation from '../../../hooks/useLocation';
import { useObservable } from '../../../hooks/useObservable';
import { prependAppPath } from '../../../services/Url';
import { isAnyAdmin } from '../../../services/User';
import { useEapState } from '../../Admin/EarlyAccessProgram/useEapState';
import { PureNavLink } from './PureNavLink';
import { getLinks, type NavLink } from './links';
import { getLinkAvailable, getLinkCurrent, getLinkDisabled } from './service';
import styles from './style.m.less';

type OuterProps = {
    isCompact?: boolean;
};

type Props = {
    links: NavLink[];
    currentLocation: string;
    currentUser: CurrentUserDto;
} & OuterProps;

function BaseNavigation({ links, currentLocation, currentUser, isCompact }: Props) {
    'use memo';
    const linkAvailable = getLinkAvailable(currentUser);
    const linkCurrent = getLinkCurrent(currentLocation);
    const linkDisabled = getLinkDisabled(currentUser);

    return (
        <nav>
            <ul className={styles.group}>
                {links.map(link => {
                    if (!linkAvailable(link)) {
                        return null;
                    }

                    const isCurrent = linkCurrent(link);
                    const isDeactivated = linkDisabled(link);

                    if (isDeactivated && !isAnyAdmin(currentUser)) {
                        return null;
                    }

                    const props = {
                        ...link,
                        isCurrent,
                        isDeactivated,
                        isCompact: !!isCompact,
                    };

                    if (link.getNotification) {
                        return <NavLinkWithNotification key={link.route} {...props} />;
                    }
                    return <PureNavLink key={link.route} {...props} />;
                })}
            </ul>
        </nav>
    );
}

export default function NavigationList(props: OuterProps) {
    'use memo';
    const appContext = useAppContext();
    const { pathname } = useLocation();
    const [eapState] = useEapState();
    const links = getLinks(eapState);

    return (
        <div className={styles.navContainer}>
            <BaseNavigation
                {...props}
                links={links}
                currentLocation={prependAppPath(pathname)}
                currentUser={appContext.user}
            />
        </div>
    );
}

function NavLinkWithNotification({
    getNotification,
    ...props
}: React.ComponentPropsWithoutRef<typeof PureNavLink> & Pick<NavLink, 'getNotification'>) {
    'use memo';
    const { value: notification } = useObservable(
        () => getNotification?.() ?? constObservable(undefined),
        [getNotification]
    );
    return <PureNavLink {...props} notification={notification} />;
}
